import React, { useEffect, useRef, useState } from "react";
import styled, { css } from "styled-components";
import { Check, CircleAlert, Copy, Network } from "lucide-react";
import Button from "common/Buttons/Button";
import { NEW_LOOK_ICON_STROKE } from "common/NewLook/layout.ts";
import { copyCodeToClipboard, openMessage } from "utils/misc";
import { getCopyMessage } from "@shared/common/CopyToClipboard/utils";
import { sharedLib } from "@shared/services/shared.service";
import { useWhitelistIPs as useSharedWhitelistIPs } from "@shared/features/configuration/hooks";
import { GetWhitelistIpsRequest, LanguageNameSpaces } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import {
  CREATE_ACCOUNT_COPY_IPS_BUTTON_ID,
  CREATE_ACCOUNT_SHOW_ALL_IPS_ID,
} from "testIds.ts";
import { focusRing } from "features/portfolio/CreateAccount/NewLook/styles";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";

interface NewLookWhitelistIPsProps {
  onCopyClick?: () => void;
  showWarning?: boolean;
  args?: GetWhitelistIpsRequest;
  exchange?: string;
}

const COPIED_FEEDBACK_MS = 1600;
const ALL_KEY = "__all__";
/** About three lines of addresses. */
const COLLAPSED_IPS = 6;

const NewLookWhitelistIPs = ({
  onCopyClick,
  showWarning,
  args,
  exchange,
}: NewLookWhitelistIPsProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const { important, mainMessage, copyText, notificationPrefix } =
    useSharedWhitelistIPs({ args, exchange });
  const { data: ips } =
    sharedLib.sharedApi.configuration.useGetWhitelistIPsQuery(args);
  const [copiedKey, setCopiedKey] = useState<string>();
  const [expanded, setExpanded] = useState(false);
  // A short list shows whole; a long one starts at three lines.
  const canCollapse = (ips?.length ?? 0) > COLLAPSED_IPS;
  const timeout = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => () => clearTimeout(timeout.current), []);

  const copy = (text: string, key: string, name: string) => {
    copyCodeToClipboard(text).catch(() => {});
    openMessage(getCopyMessage(name), "info");
    onCopyClick?.();
    setCopiedKey(key);
    clearTimeout(timeout.current);
    timeout.current = setTimeout(
      () => setCopiedKey(undefined),
      COPIED_FEEDBACK_MS
    );
  };

  const CopyAllIcon = copiedKey === ALL_KEY ? Check : Copy;

  return (
    <Card $warning={!!showWarning}>
      <Header>
        <Title>
          <Network size={16} strokeWidth={NEW_LOOK_ICON_STROKE} aria-hidden />
          {t("create_account.new_look.ip_title")}
        </Title>
        <CopyAllButton
          size="small"
          onClick={() => copy(copyText || "", ALL_KEY, notificationPrefix)}
          data-testid={CREATE_ACCOUNT_COPY_IPS_BUTTON_ID}
          icon={
            <CopyAllIcon
              size={14}
              strokeWidth={NEW_LOOK_ICON_STROKE}
              aria-hidden
            />
          }
        >
          {t("create_account.new_look.copy_all")}
        </CopyAllButton>
      </Header>
      <Description>
        <strong>{important}</strong> {mainMessage}
      </Description>
      {/* Phones: one line, so the addresses fit on screen with the guide. */}
      <ShortDescription>
        {t("create_account.new_look.ip_short")}
      </ShortDescription>
      {/* One block, not a row (and a copy button) per address: exchanges
          take the whole list pasted at once (Copy all). */}
      <Addresses>
        <AddressList $expanded={expanded || !canCollapse}>
          {ips?.join(", ")}
        </AddressList>
      </Addresses>
      {canCollapse && (
        <ShowAll
          type="button"
          onClick={() => setExpanded((value) => !value)}
          aria-expanded={expanded}
          data-testid={CREATE_ACCOUNT_SHOW_ALL_IPS_ID}
        >
          {expanded
            ? t("create_account.new_look.show_fewer_ips")
            : t("create_account.new_look.show_all_ips", {
                count: ips?.length ?? 0,
              })}
        </ShowAll>
      )}
      {showWarning && (
        <Warning role="alert">
          <CircleAlert
            size={14}
            strokeWidth={NEW_LOOK_ICON_STROKE}
            aria-hidden
          />
          {t("create_account.new_look.copy_ips_warning")}
        </Warning>
      )}
    </Card>
  );
};

export default NewLookWhitelistIPs;

const Card = styled.div<{ $warning: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 14px 16px 16px;
  border-radius: ${(p) => p.theme.radii.control}px;
  background: ${(p) => getNewLookTokens(p.theme).well};
  box-shadow: inset 0 0 0 1px
    ${(p) =>
      p.$warning
        ? getNewLookTokens(p.theme).down
        : getNewLookTokens(p.theme).hairline};
`;
const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;
const Title = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  ${newLookType.label};
  font-weight: 520;
  color: ${(p) => getNewLookTokens(p.theme).ink};
  svg {
    color: ${(p) => getNewLookTokens(p.theme).secondary};
  }
`;
const CopyAllButton = styled(Button)`
  &&& {
    height: 28px;
    padding: 0 12px;
    font-size: 12.5px;
    gap: 6px;
  }
`;
const ShortDescription = styled.div`
  display: none;
  @media (max-width: 560px) {
    display: block;
    font-size: 12.5px;
    line-height: 18px;
    color: ${(p) => getNewLookTokens(p.theme).secondary};
  }
`;
const Description = styled.div`
  @media (max-width: 560px) {
    display: none;
  }
  ${newLookType.meta};
  font-size: 12.5px;
  line-height: 18px;
  color: ${(p) => getNewLookTokens(p.theme).secondary};
  strong {
    color: ${(p) => getNewLookTokens(p.theme).text};
    font-weight: 500;
  }
`;
const Addresses = styled.div`
  margin-top: 4px;
  padding: 10px 12px;
  border-radius: ${(p) => p.theme.radii.control - 2}px;
  background: ${(p) => getNewLookTokens(p.theme).panel};
  box-shadow: inset 0 0 0 1px ${(p) => getNewLookTokens(p.theme).hairline};
`;
/* The clamp sits on the text (not the padded box), so no part of a fourth
   line shows under the third. */
const AddressList = styled.p<{ $expanded: boolean }>`
  margin: 0;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 12.5px;
  line-height: 20px;
  word-spacing: 2px;
  color: ${(p) => getNewLookTokens(p.theme).ink};
  user-select: all;
  ${(p) =>
    !p.$expanded &&
    css`
      display: -webkit-box;
      -webkit-line-clamp: 3;
      /* Phones: two lines, so the key fields below stay on screen. */
      @media (max-width: 560px) {
        -webkit-line-clamp: 2;
      }
      -webkit-box-orient: vertical;
      overflow: hidden;
    `}
`;
const ShowAll = styled.button`
  align-self: flex-start;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  font-size: 12.5px;
  font-weight: 520;
  line-height: 18px;
  color: ${(p) => getNewLookTokens(p.theme).accent};
  &:hover {
    text-decoration: underline;
  }
  ${focusRing};
`;
const Warning = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  color: ${(p) => p.theme.colors.red};
  font-size: 12.5px;
  line-height: 18px;
  svg {
    flex: none;
    margin-top: 2px;
  }
`;
